import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDown, Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  tutorialId?: string;
  showTranslateButton?: boolean;
  onTranslationRequested?: (translationId: string) => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  tutorialId, 
  showTranslateButton = false,
  onTranslationRequested 
}) => {
  const { 
    currentLanguage, 
    setLanguage, 
    availableLanguages, 
    isTranslationAvailable,
    requestTranslation 
  } = useLanguage();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationAvailable, setTranslationAvailable] = useState<boolean | null>(null);

  // Check if Spanish translation is available for this tutorial
  useEffect(() => {
    if (tutorialId && showTranslateButton) {
      isTranslationAvailable(tutorialId, 'es').then(setTranslationAvailable);
    }
  }, [tutorialId, showTranslateButton, isTranslationAvailable]);

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    setIsOpen(false);
  };

  const handleTranslateRequest = async () => {
    if (!tutorialId) return;
    
    setIsTranslating(true);
    try {
      const translationId = await requestTranslation(tutorialId, 'es');
      onTranslationRequested?.(translationId);
    } catch (error) {
      console.error('Translation request failed:', error);
      // You could show a toast notification here
    } finally {
      setIsTranslating(false);
    }
  };

  const currentLanguageInfo = availableLanguages.find(lang => lang.code === currentLanguage);
  
  // Use API result for Spanish availability; fall back to language list if null
  const isSpanishAvailable = translationAvailable ?? availableLanguages.some(lang => lang.code === 'es');
  
  // Show available languages or translate button
  const getButtonContent = () => {
    if (isSpanishAvailable) {
      // Show available languages
      return (
        <>
          <Globe className="w-4 h-4" />
          <span className="flex items-center space-x-1">
            {availableLanguages.map((lang, index) => (
              <React.Fragment key={lang.code}>
                <span>{lang.flag}</span>
                <span>{lang.code.toUpperCase()}</span>
                {index < availableLanguages.length - 1 && <span className="text-gray-400">/</span>}
              </React.Fragment>
            ))}
          </span>
          <ChevronDown className="w-4 h-4" />
        </>
      );
    } else {
      // Show translate button
      return (
        <>
          <Globe className="w-4 h-4" />
          <span>Translate to Spanish</span>
          <ChevronDown className="w-4 h-4" />
        </>
      );
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        {getButtonContent()}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-10 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            {isSpanishAvailable ? (
              // Show available languages
              availableLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 ${
                    currentLanguage === language.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span>{language.flag}</span>
                  <span>{language.name}</span>
                  {currentLanguage === language.code && (
                    <span className="ml-auto text-blue-600">✓</span>
                  )}
                </button>
              ))
            ) : (
              // Show translate option
              <button
                onClick={handleTranslateRequest}
                disabled={isTranslating}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Globe className="w-4 h-4" />
                <span>
                  {isTranslating ? 'Translating...' : 'Translate to Spanish'}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default LanguageSwitcher;
